"use client";
import { apiSpecificOffers } from '@/apis/offers';
import { apiExpiredOffers } from '@/apis/user';
import { OffersOffer, PaginationType } from '@/services/dataTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { filterOfferBanners, getBannerDimensions, getBaseImageUrl } from '@/constants/hooks';
import dynamic from 'next/dynamic';

const Banner      = dynamic(() => import('./Banner'),        { ssr: false });
const OffersListView = dynamic(() => import('./OffersListView'), { ssr: false });

interface Props {
    initialOffers: OffersOffer[];
    companyId:     string;
    merchantId:    string;
    awaited_p_id:  string;
    mer_slug_type: string;
    mer_slug:      string;
    offerBanner:   OffersOffer[];
    domain:        string;
    merchantName:  string;
    pagination:    PaginationType;
    ads_campaign:  boolean;
}

const LazyLoadingOffers = ({
    initialOffers, companyId, merchantId, awaited_p_id,
    mer_slug_type, mer_slug, offerBanner, domain,
    merchantName, pagination, ads_campaign,
}: Props) => {
    const [offers,         setOffers]         = useState<OffersOffer[]>(initialOffers);
    const [expiredOffers,  setExpiredOffers]   = useState<OffersOffer[]>([]);
    const [loading,        setLoading]         = useState(false);
    const [page,           setPage]            = useState(1);
    const [hasMore,        setHasMore]         = useState(true);
    const [filteredBanners, setFilteredBanners] = useState<OffersOffer[]>([]);
    const observer = useRef<IntersectionObserver | null>(null);
    const bannerEvery = 2;

    useEffect(() => {
        setOffers(initialOffers);
        fetchExpired();
    }, [initialOffers]);

    useEffect(() => {
        if (offerBanner?.length > 0)
            setFilteredBanners(filterOfferBanners(offerBanner, 300, 600, 50, 150));
    }, [offerBanner]);

    const fetchExpired = async () => {
        try {
            const res = await apiExpiredOffers(merchantId, companyId);
            setExpiredOffers(res.data?.offers || []);
        } catch {}
    };

    const loadMore = async () => {
        if (loading || !hasMore) return;
        if (page === pagination?.last_page) { setHasMore(false); return; }
        setLoading(true);
        try {
            const next = page + 1;
            const res  = await apiSpecificOffers(merchantId, companyId, next);
            if (res.data?.offers?.length > 0) {
                setOffers(prev => [...prev, ...res.data.offers]);
                setPage(next);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const sentinelRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (loading) return;
            observer.current?.disconnect();
            observer.current = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting && hasMore && !loading) loadMore();
            }, { threshold: 0.1 });
            if (node) observer.current.observe(node);
        },
        [loading, hasMore],
    );

    return (
        <div className="w-full space-y-4">

            {/* ── Active offers ── */}
            {offers.map((offer, index) =>
                offer.status === 1 && offer.offer.status === 1 ? (
                    <React.Fragment key={offer.id}>
                        <div ref={index === offers.length - 1 ? sentinelRef : undefined}>
                            <OffersListView
                                product={offer}
                                companyId={companyId}
                                awaited_p_id={awaited_p_id}
                                mer_slug_type={mer_slug_type}
                                mer_slug={mer_slug}
                                domain={domain}
                                ads_campaign={ads_campaign}
                            />
                        </div>

                        {/* Banner injection every N offers */}
                        {(index + 1) % bannerEvery === 0 &&
                            filteredBanners[Math.floor(index / bannerEvery)] && (() => {
                                const banner    = filteredBanners[Math.floor(index / bannerEvery)];
                                const dimension = getBannerDimensions(banner);
                                return (
                                    <div key={`banner-${index}`} className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                                        <Banner
                                            data={banner}
                                            height={dimension?.height}
                                            width={dimension?.width}
                                            offerLink={null}
                                            domain={domain}
                                            mer_slug={mer_slug}
                                            slug_type={mer_slug_type}
                                        />
                                    </div>
                                );
                            })()}
                    </React.Fragment>
                ) : null
            )}

            {/* ── Loading indicator ── */}
            {loading && (
                <div className="flex justify-center py-8">
                    <div className="flex items-center gap-2">
                        {[0, 1, 2].map(i => (
                            <div
                                key={i}
                                className="w-2.5 h-2.5 rounded-full bg-[#D61C4E] animate-bounce"
                                style={{ animationDelay: `${i * 0.15}s` }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* ── Expired offers ── */}
            {expiredOffers?.length > 0 && (
                <section className="pt-10 mt-4 border-t border-slate-100 space-y-4">
                    {/* Header */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="w-5 h-[2px] bg-slate-300 rounded-full" />
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">
                                Expired Deals
                            </span>
                        </div>
                        <h2 className="text-lg font-black text-slate-700 tracking-tight leading-none">
                            Old codes for{" "}
                            <span className="text-[#D61C4E]">{merchantName}</span>
                            <span className="text-slate-400 text-sm font-medium ml-2">
                                — might still work!
                            </span>
                        </h2>
                    </div>

                    <div className="space-y-4 opacity-60 hover:opacity-80 transition-opacity">
                        {expiredOffers.slice(0, 10).map((item, i) => (
                            <OffersListView
                                key={`expired-${i}`}
                                product={item}
                                companyId={companyId}
                                awaited_p_id={awaited_p_id}
                                mer_slug_type={mer_slug_type}
                                mer_slug={mer_slug}
                                domain={domain}
                                ads_campaign={ads_campaign}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Schema */}
            <script
                type="application/ld+json"
                id="AggregateOffer"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type":    "AggregateOffer",
                        name:        `${merchantName} Coupons & Deals`,
                        description: `Active coupons and deals for ${merchantName}.`,
                        offerCount:  offers.length,
                        offers: offers.map(o => ({
                            "@type": "Offer",
                            name:        o?.offer?.offer_title,
                            description: o?.offer?.offer_detail,
                            url: getBaseImageUrl(`${domain}`, `${o.offer?.url}`, ""),
                        })),
                    }),
                }}
            />
        </div>
    );
};

export default LazyLoadingOffers;
